import { useEffect, useRef, type RefObject } from 'react';

type ResizeHandler = (entry: ResizeObserverEntry) => void;

// A WeakMap allows the browser to garbage collect the Element when it is removed from the DOM.
// Each Element maps to a Set of handlers so multiple subscribers on the same node coexist.
const handlersMap = new WeakMap<Element, Set<ResizeHandler>>();

const getOrCreateHandlers = (target: Element): Set<ResizeHandler> => {
    let handlers = handlersMap.get(target);
    if (!handlers) {
        handlers = new Set();
        handlersMap.set(target, handlers);
    }
    return handlers;
};

let globalObserver: ResizeObserver | null = null;

const getObserver = (): ResizeObserver => {
    if (!globalObserver) {
        globalObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                handlersMap.get(entry.target)?.forEach(handler => handler(entry));
            }
        });
    }
    return globalObserver;
};

/**
 * Subscribes to size changes of the element pointed at by `targetRef` using a
 * shared, lazily-created singleton `ResizeObserver`. Multiple components may
 * observe the same element concurrently; the underlying observer is only
 * detached from an element when its last subscriber unmounts.
 *
 * `callback` is read through a ref, so passing a new function on every render
 * does NOT trigger re-subscription. The effect re-runs only when `targetRef`
 * identity or `box` changes — pass a stable ref (e.g. from `useRef`) to avoid
 * unnecessary re-subscriptions.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 */
export const useResizeObserver = <T extends Element>(
    targetRef: RefObject<T | null>,
    callback: ResizeHandler,
    box: ResizeObserverBoxOptions = 'content-box'
): void => {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const target = targetRef.current;
        if (!target) {
            return () => {};
        }

        const handler: ResizeHandler = entry => callbackRef.current(entry);
        const handlers = getOrCreateHandlers(target);
        handlers.add(handler);

        const observer = getObserver();
        observer.observe(target, { box });

        return () => {
            handlers.delete(handler);
            if (handlers.size === 0) {
                observer.unobserve(target);
                handlersMap.delete(target);
            }
        };
    }, [targetRef, box]);
};
